import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { NeedHelpButton } from "@/components/need-help-button";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import { useShadcnInsetDynamicPageTitle } from "@/hooks/useShadcnInsetBreadcrumbTrail";
import { ExecutionRulesSimulatorPanel } from "./index";
import { SIMULATOR_LIST_PATH } from "./simulatorRoutes";
import {
  WORKSPACE_HUB_BREADCRUMB,
  WORKSPACE_HUB_PATH,
  isWorkspaceHubNavState,
} from "../Workspace/workspaceRoutes";

/**
 * Standalone “new simulation” flow (same panel as before, routed under /simulator/new).
 * List + entry UX lives on {@link SimulatorListPage}.
 */
export default function SimulatorWizardPage(): JSX.Element {
  const location = useLocation();
  const shadcnInset = useShadcnInsetChrome();
  const { setActions: setInsetHeaderActions } = useShadcnInsetHeaderActions();
  const fromWorkspace = isWorkspaceHubNavState(location.state);
  const exitPath = fromWorkspace ? WORKSPACE_HUB_PATH : SIMULATOR_LIST_PATH;
  const exitLabel = fromWorkspace ? "Back to workspace" : "Back to simulator";

  useShadcnInsetDynamicPageTitle("New simulation", {
    parent: fromWorkspace ? WORKSPACE_HUB_BREADCRUMB : null,
  });

  const simulatorHelpHeaderActions = useMemo(
    () => <NeedHelpButton href="https://help.tesseratrades.com/simulator" />,
    []
  );

  useEffect(() => {
    if (!shadcnInset) {
      setInsetHeaderActions(null);
      return;
    }
    setInsetHeaderActions(simulatorHelpHeaderActions);
    return () => setInsetHeaderActions(null);
  }, [setInsetHeaderActions, shadcnInset, simulatorHelpHeaderActions]);

  return (
    <ExecutionRulesSimulatorPanel
      variant="standalone"
      wizardExitHref={exitPath}
      wizardExitLabel={exitLabel}
      savedSimulationsHref={exitPath}
      simulatorWizardLayout
    />
  );
}
